import { EventEmitterHelper } from '../Utility/EventEmitter';
import {
    MercuryFxClient,
    MercuryFxEvents,
    RpcHandler,
    RpcName,
    SubscriptionResult,
} from '@reachmedianetwork/mercury-fx-client';
import { Log } from '../AuxillaryService/Log';
import { DataEvent } from '@reachmedianetwork/eventing-abstractions';
import { MessagingClientEvent } from './Events';
import { Deferred } from '../Utility/Deferred';
import { IMessagingClient } from '.';
import { SignageLogItem } from '@reachmedianetwork/signage-abstractions';
import { retrievePlayerIamToken } from '../ReachIam';
import {
    DuplicateRpcDeclarationError,
    MessagingClientConnectionError,
    MessagingClientError,
    MessagingClientSubscriptionError,
    UndefinedRpcError,
} from './Errors';
import { isNullish, Nullish } from '../Utility/Nullish';
import { IRpcHost } from './IRpcHost';
import { IPushReceiver } from './IPushReceiver';
import { ILogRelay } from '../Logging/ILogRelay';
import { ActorType } from '@reachmedianetwork/reach-authentication-abstractions';
import { CustomRpcHandler } from './types';

function serializeArgs(...args: unknown[]) {
    if (args && args.length > 0) {
        return args.reduce((memo: string, arg) => {
            return memo + '\n' + JSON.stringify(arg);
        }, '; args=');
    }

    return '';
}

type FullMessagingClient = IMessagingClient & IPushReceiver & IRpcHost & ILogRelay;

class MessagingClient extends EventEmitterHelper implements FullMessagingClient {
    private readonly _playerUuid: string | Nullish;
    private readonly _platformHardwareId: string | Nullish;
    private readonly _additionalClientProps: Record<string, string>;
    private _subscribedDeferred?: Deferred<void>;
    private _connectionDeferred?: Deferred<void>;
    private _client?: MercuryFxClient;
    private _subscriptions?: SubscriptionResult[];
    private _rpcHandlers: Array<{
        name: keyof typeof RpcName;
        handler: RpcHandler<keyof typeof RpcName>;
    }>;
    private _customRpcHandlers: Array<{
        name: string;
        handler: CustomRpcHandler;
    }>;

    constructor(
        playerUuid: string | Nullish,
        platformHardwareId: string | Nullish,
        additionalClientProps: Record<string, string>
    ) {
        super();
        this.setEventEmitterElement(document);

        this._playerUuid = playerUuid;
        this._platformHardwareId = platformHardwareId;
        this._additionalClientProps = additionalClientProps;
        this._rpcHandlers = [];
        this._customRpcHandlers = [];
    }

    private _initializeClient() {
        if (!this._playerUuid) {
            throw new MessagingClientConnectionError('Player UUID not set.');
        }
        if (!this._platformHardwareId) {
            throw new MessagingClientConnectionError('Platform Hardware ID not set.');
        }

        this._client = new MercuryFxClient({
            baseUrl: process.env.BASE_URL_MERCURYFX,
            vingInterval: 60000,
            additionalClientProps: this._additionalClientProps,
        })
            .withIdentity(ActorType.PLAYER, this._playerUuid, this._platformHardwareId)
            .withTokenFactory(() => {
                return retrievePlayerIamToken();
            })
            .useAutoSubscribe(60000);

        this._client.on('error', (_sender, state, error) =>
            Log.Error(
                `MercuryFxClient error (state=${state}): ${error.message}`,
                null,
                __fileline,
                error
            )
        );
        this._client.on('state changed', (_sender, newState, oldState) =>
            Log.Debug(
                `MercuryFxClient changed from state "${oldState}" to "${newState}"`,
                null,
                __fileline
            )
        );
        this._client.on('warn', (_sender: unknown, message: string) =>
            Log.Warning(message, null, __fileline)
        );
        //logging debug information creates a feedback loop whereby the relay
        //of the log item is logged which is in turn relayed which is then
        //logged... find a way to fix this
        /*
        this._client.on( 'debug', ( _sender: unknown, message, ...args )
        => Log.Debug( message + serializeArgs( ...args ), null, __fileline ));
        */
        this._client.on('subscribed', this._onSubscribedReceived);
        this._client.on('push', this._onPushReceived);

        // these functions are used by testing dev code. They will be removed
        // after MercuryFx is rolled out as they are not part of the public
        // IMessagingClient API
        /* eslint-disable @typescript-eslint/no-explicit-any */
        this.on = this._client.on.bind(this._client) as any;
        this.once = this._client.once.bind(this._client) as any;
        this.off = this._client.off.bind(this._client) as any;
        /* eslint-enable @typescript-eslint/no-explicit-any */
    }

    private _connectRpc(): void {
        if (isNullish(this._client)) {
            return;
        }

        while (this._rpcHandlers.length > 0) {
            const rpc = this._rpcHandlers.shift();
            if (rpc === undefined) break;
            this._client.declareRpc(rpc.name, rpc.handler);
        }
    }

    /* eslint-disable @typescript-eslint/no-explicit-any */
    public on?: (
        event: typeof MercuryFxEvents[number],
        listener: (...args: any[]) => void
    ) => MercuryFxClient | undefined;
    public once?: (
        event: typeof MercuryFxEvents[number],
        listener: (...args: any[]) => void
    ) => MercuryFxClient | undefined;
    public off?: (
        event: typeof MercuryFxEvents[number],
        listener: (...args: any[]) => void
    ) => MercuryFxClient | undefined;
    /* eslint-enable @typescript-eslint/no-explicit-any */

    /**
     * Connects to the MercuryFx cluster. Method will automatically retry
     * connection indefinitely.
     */
    public async connect(): Promise<void> {
        if (this._connectionDeferred) {
            await this._connectionDeferred.promise;

            return;
        }

        this._connectionDeferred = new Deferred();
        this._subscribedDeferred = new Deferred();

        try {
            this._initializeClient();
            await this._client?.connect();
        } catch (error) {
            if (error instanceof MessagingClientConnectionError) {
                throw error;
            } else {
                throw new MessagingClientConnectionError(String(error));
            }
        }

        this._connectRpc();

        this._connectionDeferred.resolve();
    }

    /**
     * MercuryFx does not support ad-hoc subscriptions.
     * @deprecated
     * @throws {MessagingClient.ConnectionError}
     * @throws {MessagingClient.SubscriptionError}
     */
    public async subscribe(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _channelId: string,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _lifestyle: 'Singleton' | 'Broadcast'
    ): Promise<void> {
        //there is no reasonable way to convert an arbitrary Mercury channelId
        //into a MercuryFx topic name.

        Log.Warning(
            'MessagingClient.subscribe is deprecated. Invocation of this method has not effect.',
            null,
            __fileline
        );
    }

    /**
     * MercuryFx does not support ad-hoc subscriptions.
     * @deprecated
     * @throws {MessagingClient.ConnectionError}
     * @throws {MessagingClient.SubscriptionError}
     */
    public async subscribeToCalendar(calendarId: string | number): Promise<void> {
        this._verifySubscriptionCreated(`data:calendar:${calendarId}`);
    }

    /**
     * MercuryFx does not support ad-hoc subscriptions.
     * @deprecated
     * @throws {MessagingClient.ConnectionError}
     * @throws {MessagingClient.SubscriptionError}
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public async subscribeToCustomHtml(_identifier: string): Promise<void> {
        //custom HTML does not get its own special channel in Mercuryfx.
        //Instead, the player is subscribed to "facility" channels which custom
        //html messages must be sent to.
    }

    /**
     * MercuryFx does not support ad-hoc subscriptions.
     * @deprecated
     * @throws {MessagingClient.ConnectionError}
     * @throws {MessagingClient.SubscriptionError}
     */
    public async subscribeToAnnouncement(identifier: string): Promise<void> {
        this._verifySubscriptionCreated(`data:announcement:${identifier}`);
    }

    /**
     * MercuryFx does not support ad-hoc subscriptions.
     * @deprecated
     * @throws {MessagingClient.ConnectionError}
     * @throws {MessagingClient.SubscriptionError}
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public async subscribeToPlayerUuid(_uuid: string): Promise<void> {
        //player is always subscribed to its own "channel"
    }

    /**
     * MercuryFx does not support ad-hoc subscriptions.
     * @deprecated
     * @throws {MessagingClient.ConnectionError}
     * @throws {MessagingClient.SubscriptionError}
     */
    public async subscribeToWeather(location: string): Promise<void> {
        this._verifySubscriptionCreated(`data:weather:${location}`);
    }

    /**
     * MercuryFx does not support ad-hoc subscriptions.
     * @deprecated
     * @throws {MessagingClient.ConnectionError}
     * @throws {MessagingClient.SubscriptionError}
     */
    public async subscribeToList(listId: string): Promise<void> {
        this._verifySubscriptionCreated(`data:list:${listId}`);
    }

    /**
     * @inheritdoc
     */
    public get canRelayLogs(): boolean {
        // logs can be relayed via Mercury FX
        return true;
    }

    /**
     * @inheritdoc
     */
    public get isConnected(): boolean {
        return this._client?.state === 'open';
    }

    private async _verifySubscriptionCreated(topicName: string): Promise<void> {
        await this._subscribedDeferred?.promise;

        if (!this._subscriptions) {
            Log.Warning(
                `widget called subscription method before subscriptions were created; topicName: ${topicName}`,
                null,
                __fileline
            );
        } else {
            if (!this._subscriptions.some((subscription) => subscription.topic === topicName)) {
                throw new MessagingClientSubscriptionError(
                    `MercuryFx client is not subscribed to topic ${topicName}`
                );
            } else {
                Log.Debug(
                    `successfully asserted that MercuryFx client is subscribed to topic ${topicName}`,
                    null,
                    __fileline
                );
            }
        }
    }

    private _onSubscribedReceived = (_sender: unknown, subscriptions: SubscriptionResult[]) => {
        Log.Debug(
            'Received MercuryFx subscriptions' + serializeArgs(subscriptions),
            null,
            __fileline
        );

        this._subscriptions = subscriptions;

        this._subscribedDeferred?.resolve();
    };

    private _onPushReceived = (event: DataEvent) => {
        Log.Debug('Received MercuryFx push' + serializeArgs(event.toObject()), null, __fileline);

        switch (event.name) {
            case 'calendar': {
                const newEvent: MessagingClientEvent['calendar-update']['detail'] = {
                    calendarId: event.key,
                };

                this.eventEmitter.emitEvent('calendar-update', newEvent);

                break;
            }
            case 'list': {
                const newEvent: MessagingClientEvent['list-update']['detail'] = {
                    listId: event.key,
                };

                this.eventEmitter.emitEvent('list-update', newEvent);

                break;
            }
            case 'playlist': {
                const newEvent: MessagingClientEvent['playlist-update']['detail'] = {
                    playlistId: event.key,
                };

                this.eventEmitter.emitEvent('playlist-update', newEvent);

                break;
            }
            case 'announcement': {
                const newEvent: MessagingClientEvent['announcement-update']['detail'] = {
                    screenid: event.key,
                };

                this.eventEmitter.emitEvent('announcement-update', newEvent);

                break;
            }
            case 'weather': {
                const newEvent: MessagingClientEvent['weather-update']['detail'] = {
                    location: event.key,
                };

                this.eventEmitter.emitEvent('weather-update', newEvent);

                break;
            }
            case 'alert_configuration': {
                const newEvent: MessagingClientEvent['emergency-alert-feed-update']['detail'] = {
                    alertConfigId: event.key,
                };

                this.eventEmitter.emitEvent('emergency-alert-feed-update', newEvent);

                break;
            }

            case 'content_feed': {
                const newEvent: MessagingClientEvent['content-feed-update']['detail'] = {
                    contentType: event.key,
                };
                this.eventEmitter.emitEvent('content-feed-update', newEvent);
                break;
            }
            case 'facility': {
                if (!event.customData || typeof event.customData !== 'object') {
                    Log.Warning('received facility push without custom data', null, __fileline);

                    return;
                }

                const customData = event.customData as {
                    type?: string;
                    target?: string;
                    payload?: unknown;
                };

                switch (customData.type?.toString()) {
                    case 'customhtml': {
                        Log.Debug(
                            `emitting custom-html-update event with identifier "${customData.target}"`,
                            null,
                            __fileline
                        );

                        this.eventEmitter.emitEvent('custom-html-update', {
                            identifier: customData.target,
                            data: customData.payload,
                        });

                        break;
                    }
                    default:
                        Log.Warning(
                            `unexpected facility push type "${customData.type}"`,
                            null,
                            __fileline
                        );

                        break;
                }
                break;
            }
            default:
                Log.Warning(
                    `MercuryFx sent an unfamiliar push event name "${event.name}"`,
                    null,
                    __fileline
                );
                break;
        }
    };

    /**
     * @inheritdoc
     */
    public async processLogs(logItems: SignageLogItem[]): Promise<void> {
        if (!this._client) {
            throw new MessagingClientError('MercuryFx failed to initialize.');
        }
        await this._client.invokeOneWayRpc('processLogs', { logItems });
    }

    /**
     * @inheritDoc
     */
    public declareRpc<T extends keyof typeof RpcName>(name: T, handler: RpcHandler<T>): void {
        if (this._rpcHandlers.some((definition) => definition.name === name)) {
            throw new DuplicateRpcDeclarationError(`RPC "${name}" was declared more than once`);
        }

        this._rpcHandlers.push({ name, handler });
        this._connectRpc();
    }

    /**
     * @inheritDoc
     */
    public declareCustomRpc(customRpcName: string, handler: CustomRpcHandler): void {
        if (this._customRpcHandlers.some((definition) => definition.name === customRpcName)) {
            throw new DuplicateRpcDeclarationError(
                `Custom RPC "${customRpcName}" was declared more than once`
            );
        }

        this._customRpcHandlers.push({ name: customRpcName, handler });
    }

    /**
     * @inheritDoc
     */
    public invokeCustomRpc(customRpcName: string, customArgs: unknown[]): Promise<unknown> {
        const definition = this._customRpcHandlers.find(
            (definition) => definition.name === customRpcName
        );

        if (isNullish(definition)) {
            throw new UndefinedRpcError(`Custom RPC "${customRpcName}" has not been declared`);
        }

        return definition.handler(customArgs);
    }

    /**
     * @inheritDoc
     */
    public addListener<TType extends keyof MessagingClientEvent>(
        type: TType,
        listener: (evt: MessagingClientEvent[TType]) => void
    ): void {
        this.eventEmitter.addListener(type, listener);
    }
}

export { MessagingClient };
